import { useState } from "react";
import { compareNullLast } from "@/utils/sortUtils";
import { avg, sum } from "./KeywordThemeShared";
import type { KeywordTheme, KeywordThemeKeyword } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";

export type ThemeSortField = "keyword" | "rank" | "volume" | "kps" | "rel" | "serpMatch" | "trend" | "keywordSales" | "asinDensity" | "competition";
export type SortDir = "asc" | "desc";

function sortValueForField(field: ThemeSortField, kw: Keyword): number | null {
  switch (field) {
    case "rank":         return kw.hero_rank;
    case "volume":       return kw.search_volume;
    case "kps":          return kw.kps;
    case "rel":          return kw.hero_relevance;
    case "serpMatch":    return kw.serp_match_pct;
    case "trend":        return kw.search_volume_trend != null ? Number(kw.search_volume_trend) : null;
    case "keywordSales": return kw.keyword_sales;
    case "asinDensity":  return kw.asin_density;
    case "competition":  return kw.competitors;
    default:             return null;
  }
}

export function useThemeSort(
  themes: KeywordTheme[],
  masterKeywords: Keyword[],
  keywordMap: Map<number, Keyword>,
) {
  const [sortField, setSortField] = useState<ThemeSortField>("kps");
  const [sortDir, setSortDir] = useState<SortDir>("desc");

  const handleSort = (field: ThemeSortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir(field === "rank" || field === "keyword" ? "asc" : "desc");
    }
  };

  const themedKeywordIds = new Set(themes.flatMap((t) => t.keywords.map((k) => k.keyword_id)));
  const unthemed = masterKeywords.filter((k) => !themedKeywordIds.has(k.id));

  // Sort themes by aggregate stat
  const sortedThemes = [...themes].sort((a, b) => {
    if (sortField === "keyword") {
      return sortDir === "asc"
        ? a.name.localeCompare(b.name)
        : b.name.localeCompare(a.name);
    }
    const kwsA = a.keywords.map((tk) => keywordMap.get(tk.keyword_id)).filter(Boolean) as Keyword[];
    const kwsB = b.keywords.map((tk) => keywordMap.get(tk.keyword_id)).filter(Boolean) as Keyword[];
    const aggregate = (kws: Keyword[]) => {
      switch (sortField) {
        case "rank":   return avg(kws.map((k) => k.hero_rank));
        case "volume": return sum(kws.map((k) => k.search_volume));
        case "kps":    return avg(kws.map((k) => k.kps));
        case "rel":          return avg(kws.map((k) => k.hero_relevance));
        case "trend":        return avg(kws.map((k) => (k.search_volume_trend != null ? Number(k.search_volume_trend) : null)));
        case "keywordSales": return avg(kws.map((k) => k.keyword_sales));
        case "asinDensity":  return avg(kws.map((k) => k.asin_density));
        case "competition":  return avg(kws.map((k) => k.competitors));
        default:             return null;
      }
    };
    return compareNullLast(aggregate(kwsA), aggregate(kwsB), sortDir);
  });

  // Sort keywords within a theme by the active field
  const sortThemeKeywords = (kws: { tk: KeywordThemeKeyword; kw: Keyword }[]) => {
    if (sortField === "keyword") {
      return [...kws].sort((a, b) =>
        sortDir === "asc"
          ? a.kw.search.localeCompare(b.kw.search)
          : b.kw.search.localeCompare(a.kw.search),
      );
    }
    return [...kws].sort((a, b) =>
      compareNullLast(sortValueForField(sortField, a.kw), sortValueForField(sortField, b.kw), sortDir),
    );
  };

  // Sort unthemed keywords by the active field too
  const sortedUnthemed = (() => {
    if (sortField === "keyword") {
      return [...unthemed].sort((a, b) =>
        sortDir === "asc" ? a.search.localeCompare(b.search) : b.search.localeCompare(a.search),
      );
    }
    return [...unthemed].sort((a, b) =>
      compareNullLast(sortValueForField(sortField, a), sortValueForField(sortField, b), sortDir),
    );
  })();

  return { sortField, sortDir, handleSort, sortedThemes, sortThemeKeywords, sortedUnthemed, unthemed };
}
